/**
 * Template manager page
 * Upload, edit, delete product templates
 */
import React, { useState, useMemo } from 'react';
import { useTemplates } from '@/hooks/useTemplates';
import { modalApi } from '@/services/modalApi';
import { imageUrlService } from '@/services/imageUrlService';
import { ProductTemplate } from '@/types';
import TemplateUpload from '@/components/admin/TemplateUpload';
import TemplateImagePreview from '@/components/admin/TemplateImagePreview';
import TemplateEditModal, { EditTemplateData } from '@/components/admin/TemplateEditModal';
import { Plus, Edit, Trash2, Loader2, Image as ImageIcon, ChevronUp, ChevronDown, Search } from 'lucide-react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface PreviewModalState {
  isOpen: boolean;
  imageUrl?: string;
  templateName: string;
}

export default function TemplateManager() {
  const [activeTab, setActiveTab] = useState<'phone_case' | 'coffee_cup'>('phone_case');
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewModal, setPreviewModal] = useState<PreviewModalState>({ isOpen: false, templateName: '' });
  const [editTemplate, setEditTemplate] = useState<ProductTemplate | null>(null);
  const { templates, loading } = useTemplates(activeTab, true); // Include inactive templates for admin

  // Filter templates by search query
  const filteredTemplates = useMemo(() => {
    return templates.filter(t =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [templates, searchQuery]);

  const handleDelete = async (id: string) => {
    if (!confirm('Xóa mẫu này? Hành động này không thể hoàn tác.')) {
      return;
    }

    try {
      await modalApi.deleteTemplate(id);
      alert('Đã xóa mẫu thành công');
    } catch (error: any) {
      alert(`Xóa thất bại: ${error.message}`);
      console.error('Delete error:', error);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'product_templates', id), {
        is_active: !currentStatus,
        updated_at: new Date()
      });
    } catch (error: any) {
      alert(`Cập nhật thất bại: ${error.message}`);
      console.error('Update error:', error);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      // First, unset all other defaults for this product type
      const otherTemplates = templates.filter(t => t.id !== id && t.is_default);
      const updatePromises = otherTemplates.map(t =>
        updateDoc(doc(db, 'product_templates', t.id), {
          is_default: false,
          updated_at: new Date()
        })
      );

      // Then set this one as default
      await Promise.all([
        ...updatePromises,
        updateDoc(doc(db, 'product_templates', id), {
          is_default: true,
          updated_at: new Date()
        })
      ]);
    } catch (error: any) {
      alert(`Update failed: ${error.message}`);
      console.error('Update error:', error);
    }
  };

  const handlePreviewImage = (template: any) => {
    // Use CDN URLs if available, fallback to Modal paths
    let imageUrl = template.cdn_thumbnail_url || template.cdn_url;

    if (!imageUrl) {
      // Fallback to Modal URLs via API
      imageUrl = imageUrlService.getTemplateImageUrl(
        template.image_path,
        template.thumbnail_path,
        template.updated_at || template.created_at
      );
    }

    setPreviewModal({
      isOpen: true,
      imageUrl: imageUrl || '',
      templateName: template.name
    });
  };

  const handleEditTemplate = (template: ProductTemplate) => {
    setEditTemplate(template);
  };

  const handleSaveTemplate = async (data: EditTemplateData) => {
    if (!editTemplate) return;

    try {
      await modalApi.updateTemplate(editTemplate.id, data);

      // Wait for Firestore to update and propagate changes
      // Increased from 500ms to 2000ms to ensure complete synchronization
      // This allows time for backend to process, upload CDN, and Firestore to replicate
      await new Promise(resolve => setTimeout(resolve, 2000));

      setEditTemplate(null);
      alert('Cập nhật thành công! Ảnh sẽ cập nhật ngay.');
    } catch (error: any) {
      throw new Error(`Update failed: ${error.message}`);
    }
  };

  const handleMoveUp = async (template: any, index: number) => {
    if (index === 0) return; // Already at top

    const previousTemplate = filteredTemplates[index - 1];
    const currentOrder = template.display_order;
    const previousOrder = previousTemplate.display_order;

    try {
      await Promise.all([
        updateDoc(doc(db, 'product_templates', template.id), {
          display_order: previousOrder,
          updated_at: new Date()
        }),
        updateDoc(doc(db, 'product_templates', previousTemplate.id), {
          display_order: currentOrder,
          updated_at: new Date()
        })
      ]);
    } catch (error: any) {
      alert(`Update failed: ${error.message}`);
      console.error('Move error:', error);
    }
  };

  const handleMoveDown = async (template: any, index: number) => {
    if (index === filteredTemplates.length - 1) return; // Already at bottom

    const nextTemplate = filteredTemplates[index + 1];
    const currentOrder = template.display_order;
    const nextOrder = nextTemplate.display_order;

    try {
      await Promise.all([
        updateDoc(doc(db, 'product_templates', template.id), {
          display_order: nextOrder,
          updated_at: new Date()
        }),
        updateDoc(doc(db, 'product_templates', nextTemplate.id), {
          display_order: currentOrder,
          updated_at: new Date()
        })
      ]);
    } catch (error: any) {
      alert(`Update failed: ${error.message}`);
      console.error('Move error:', error);
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Mẫu sản phẩm</h1>
          <p className="text-gray-600">
            Quản lý mẫu sản phẩm cho ốp lưng điện thoại và cốc cà phê
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Tải lên mẫu
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('phone_case')}
            className={`px-4 py-2 font-medium border-b-2 transition ${
              activeTab === 'phone_case'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Ốp lưng điện thoại
          </button>
          <button
            onClick={() => setActiveTab('coffee_cup')}
            className={`px-4 py-2 font-medium border-b-2 transition ${
              activeTab === 'coffee_cup'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Cốc cà phê
          </button>
        </div>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <div className="mb-6">
          <TemplateUpload
            onSuccess={() => {
              setShowUpload(false);
              alert('Đã tải lên mẫu thành công!');
            }}
            onCancel={() => setShowUpload(false)}
          />
        </div>
      )}

      {/* Search Bar */}
      {templates.length > 0 && (
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm kiếm theo tên hoặc danh mục..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
        </div>
      )}

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Không tìm thấy mẫu</p>
          <button
            onClick={() => setShowUpload(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
          >
            Tải lên mẫu đầu tiên
          </button>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Không tìm thấy mẫu phù hợp</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template, index) => (
            <div
              key={template.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition"
            >
              {/* Thumbnail */}
              <div className="aspect-square bg-gray-100 relative">
                {(template.cdn_thumbnail_url || template.cdn_url || template.thumbnail_path || template.image_path) ? (
                  <img
                    src={template.cdn_thumbnail_url || template.cdn_url || imageUrlService.getTemplateImageUrl(
                      template.image_path,
                      template.thumbnail_path,
                      template.updated_at || template.created_at
                    )}
                    alt={template.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="300" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                {template.is_default && (
                  <div className="absolute top-2 right-2 bg-indigo-600 text-white text-xs font-medium px-2 py-1 rounded">
                    Mặc định
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>Danh mục: {template.category}</div>
                  <div>Chất liệu: {template.material}</div>
                </div>

                {/* Actions */}
                <div className="mt-4 space-y-2">
                  {/* Primary Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePreviewImage(template)}
                      className="flex-1 px-3 py-2 text-sm rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition flex items-center justify-center gap-1"
                      title="Xem ảnh"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Xem
                    </button>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="flex-1 px-3 py-2 text-sm rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition flex items-center justify-center gap-1"
                      title="Sửa"
                    >
                      <Edit className="w-4 h-4" />
                      Sửa
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Xóa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Secondary Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleMoveUp(template, index)}
                      disabled={index === 0}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Lên"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(template, index)}
                      disabled={index === filteredTemplates.length - 1}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Xuống"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSetDefault(template.id)}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg transition ${
                        template.is_default
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {template.is_default ? 'Mặc định' : 'Đặt làm mặc định'}
                    </button>
                    <button
                      onClick={() => handleToggleActive(template.id, template.is_active)}
                      className={`px-3 py-2 text-sm rounded-lg transition ${
                        template.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {template.is_active ? 'Hoạt' : 'Tắt'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <TemplateImagePreview
        isOpen={previewModal.isOpen}
        imageUrl={previewModal.imageUrl}
        templateName={previewModal.templateName}
        onClose={() => setPreviewModal({ isOpen: false, templateName: '' })}
      />

      <TemplateEditModal
        isOpen={editTemplate !== null}
        template={editTemplate}
        onClose={() => setEditTemplate(null)}
        onSave={handleSaveTemplate}
      />
    </div>
  );
}

